import { Phone, PhoneOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { IRingingCall } from '../../types';
import { CallerIdentity } from './caller-identity';

interface RingingQueueProps {
    readonly queue: readonly IRingingCall[];
    /** Finish-first: answering is only enabled while no call is active. */
    readonly canAnswer: boolean;
    readonly onAnswer: (callId: string) => void;
    readonly onDecline: (callId: string) => void;
}

/**
 * The ringing queue — one row per inbound call waiting on this agent,
 * rendered atop the widget body in EVERY state (a single row while idle IS
 * the incoming-call UI). Decline always works; Answer only while idle, so
 * the agent finishes the current call first.
 */
export function RingingQueue({ queue, canAnswer, onAnswer, onDecline }: RingingQueueProps) {
    if (queue.length === 0) return null;

    return (
        <div
            data-slot="voice-ringing-queue"
            className="flex flex-col divide-y divide-white/40 border-b border-white/40 bg-success-500/[0.06] dark:divide-white/[0.06] dark:border-white/[0.06] dark:bg-success-500/[0.1]"
        >
            {queue.map((ringing) => {
                const caller = ringing.contact?.name ?? ringing.fromNumber;
                const line =
                    ringing.channelIdentity.displayName ?? ringing.channelIdentity.identity;
                return (
                    <div
                        key={ringing.callId}
                        title={`Incoming call · ${line}`}
                        className="flex items-center gap-2 px-3 py-2"
                    >
                        <span className="relative flex size-2 shrink-0" aria-hidden>
                            <span className="absolute inline-flex size-full animate-ping rounded-full bg-success-400 opacity-75" />
                            <span className="relative inline-flex size-2 rounded-full bg-success-500" />
                        </span>
                        <CallerIdentity
                            name={ringing.contact?.name ?? null}
                            number={ringing.fromNumber}
                            size="sm"
                            className="min-w-0 flex-1 gap-2"
                        />
                        <Button
                            type="button"
                            variant="destructive"
                            size="icon-sm"
                            aria-label={`Decline call from ${caller}`}
                            className="rounded-full shadow-sm"
                            onClick={() => onDecline(ringing.callId)}
                        >
                            <PhoneOff className="size-3.5" />
                        </Button>
                        <Button
                            type="button"
                            size="icon-sm"
                            disabled={!canAnswer}
                            title={canAnswer ? undefined : 'Finish the current call first'}
                            aria-label={`Answer call from ${caller}`}
                            className="rounded-full bg-success-500 text-white shadow-[0_4px_12px_-3px_rgb(34_197_94/0.5)] hover:bg-success-600 dark:shadow-[0_4px_12px_-3px_rgb(34_197_94/0.4)]"
                            onClick={() => onAnswer(ringing.callId)}
                        >
                            <Phone className="size-3.5" />
                        </Button>
                    </div>
                );
            })}
        </div>
    );
}
